import React, { FC, Fragment, useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
    getDefaultCustomerSettingsTemplate,
    getCustomerSettingsByIdTemplate,
} from 'apollo/templates/organization/query.graphql';
import { saveOrganizationCustomerSettingsTemplate } from 'apollo/templates/organization/mutation.graphql';
import { Typography, Box } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { CustomerSetting } from 'models/customer-setting';
import { CustomerSettingsList } from 'components/customer-settings/customer-settings-list';
import SkeletonTable from 'components/customer-settings/skeleton-table.component';

type GroupedSettings = Record<string, CustomerSetting[]>;

const CustomerSettings: FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t: translate } = useTranslation(['customer-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [data, setData] = useState<GroupedSettings>({});
    const [originalData, setOriginalData] = useState<GroupedSettings>({});
    const [customerInfo, setCustomerInfo] = useState<any>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Query 1: Fetch customer data (if id exists)
    const {
        data: customerData,
        loading: loadingCustomer,
        error: errorCustomer,
        refetch: refetchCustomerData,
    } = useQuery(getCustomerSettingsByIdTemplate, {
        variables: { organizationId: id },
        skip: !id,
    });

    // Query 2: Fetch default settings
    const {
        data: defaultData,
        loading: loadingDefault,
        error: errorDefault,
    } = useQuery(getDefaultCustomerSettingsTemplate);

    // Mutation for saving settings
    const [saveSettings, { loading: saving }] = useMutation(saveOrganizationCustomerSettingsTemplate, {
        onCompleted: (data) => {
            if (data?.saveOrganizationCustomerSettings?.success) {
                refetchCustomerData();
                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('customer-settings:SUCCESS_TOAST_MESSAGE'),
                });
            } else {
                const errors = data?.saveOrganizationCustomerSettings?.errors || [];
                const errorMap = errors.reduce((acc: Record<string, string>, error: any) => {
                    acc[error.field] = error.message;
                    return acc;
                }, {});

                setValidationErrors(errorMap);

                // Scroll to first error
                if (errors.length > 0) {
                    setTimeout(() => {
                        const firstErrorField = errors[0].field;
                        const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
                        if (errorElement) {
                            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 100);
                }

                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('customer-settings:ERROR_TOAST_MESSAGE'),
                });
            }
        },
        onError: (error) => {
            console.error('Error saving settings:', error);
        },
    });

    // Merge both when ready
    useEffect(() => {
        if (!defaultData || loadingCustomer) return;

        const defaultSettings = defaultData?.getDefaultCustomerSettings || [];
        const customerSettings = customerData?.getCustomerSettingsById?.customerSettings || [];
        const info = customerData?.getCustomerSettingsById || null;

        setCustomerInfo(info);
        const settingsMap = customerSettings.reduce((acc: any, setting: any) => {
            acc[setting.name] = { value: setting.value, _id: setting._id };
            return acc;
        }, {});

        const grouped = defaultSettings.reduce((acc: GroupedSettings, item: CustomerSetting) => {
            acc[item.category as string] = acc[item.category as string] || [];
            const settingData = settingsMap[item.name];
            const preSelectedValue = settingData ? settingData.value : '';
            const _id = settingData ? settingData._id : undefined;
            acc[item.category as string].push({
                ...item,
                preSelectedValue,
                _id,
            });
            return acc;
        }, {} as GroupedSettings);

        setData(grouped);
        setOriginalData(grouped);
    }, [defaultData, customerData, loadingCustomer]);

    const handleValueChange = useCallback(
        (category: string, name: string, value: any) => {
            setData((prev) => ({
                ...prev,
                [category]: prev[category].map((item) =>
                    item.name === name ? { ...item, preSelectedValue: value } : item,
                ),
            }));

            // Clear validation error for this field
            if (validationErrors[name]) {
                setValidationErrors((prev) => {
                    const { [name]: _, ...rest } = prev;
                    return rest;
                });
            }
        },
        [validationErrors],
    );

    const getDisplayValue = useCallback((val: any) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object' && Object.keys(val).length === 0) return '';
        if (Array.isArray(val) && val.length === 0) return '';
        return String(val);
    }, []);

    const formatValue = useCallback((val: any) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
            try {
                return JSON.stringify(val, null, 2);
            } catch {
                return String(val);
            }
        }
        return val.toString();
    }, []);

    const detectCustomType = useCallback((defaultValue: any) => {
        if (defaultValue === null || defaultValue === undefined || typeof defaultValue === 'string') {
            return 'string';
        }

        if (Array.isArray(defaultValue)) {
            if (defaultValue.length > 0 && typeof defaultValue[0] === 'object' && !Array.isArray(defaultValue[0])) {
                return 'arrayOfObject';
            }
            return 'Array';
        }

        if (typeof defaultValue === 'object' && defaultValue !== null) {
            return 'Object';
        }

        if (typeof defaultValue === 'number') {
            return 'number';
        }

        return typeof defaultValue;
    }, []);

    const changedData = useMemo(() => {
        const changes: any[] = [];
        const defaultSettings = defaultData?.getDefaultCustomerSettings || [];
        const customerSettings = customerData?.getCustomerSettingsById?.customerSettings || [];

        Object.keys(data).forEach((category) => {
            data[category].forEach((setting) => {
                const originalSetting = originalData[category]?.find((s) => s.name === setting.name);
                if (originalSetting && originalSetting.preSelectedValue !== setting.preSelectedValue) {
                    const customerSetting = customerSettings.find((cs: any) => cs.name === setting.name);
                    const originalValue = customerSetting ? customerSetting.value : null;
                    const currentValue = setting.preSelectedValue || null;

                    // Include change if current value is different from original customer value
                    if (originalValue !== currentValue) {
                        const defaultSetting = defaultSettings.find((ds: any) => ds.name === setting.name);
                        const originalType = detectCustomType(defaultSetting?.defaultValue) || setting.type;
                        const changeData: any = {
                            name: setting.name,
                            value: setting.preSelectedValue,
                            type: originalType,
                        };
                        if (setting._id) {
                            changeData._id = setting._id;
                        }
                        changes.push(changeData);
                    }
                }
            });
        });
        return changes;
    }, [data, originalData, defaultData, customerData, detectCustomType]);

    const handleSave = useCallback(() => {
        if (changedData.length === 0) return;
        setValidationErrors({});

        saveSettings({
            variables: {
                organizationId: id,
                settings: changedData,
            },
        });
    }, [changedData, saveSettings, id]);

    // Handle loading and error
    const isLoading = useMemo(() => loadingCustomer || loadingDefault, [loadingCustomer, loadingDefault]);
    const hasError = useMemo(() => errorCustomer || errorDefault, [errorCustomer, errorDefault]);

    if (hasError) {
        return (
            <Typography color="error" sx={{ mt: 2 }}>
                {translate('customer-settings:FAILED_TO_LOAD_SETTINGS')}
            </Typography>
        );
    }

    return (
        <Fragment>
            <PageTitle
                headingStyle="h1"
                title={
                    isLoading
                        ? `${translate('customer-settings:CUSTOMER_SETTINGS')}`
                        : `${translate('customer-settings:CUSTOMER_SETTINGS_FOR')} ${customerInfo?.name || translate('customer-settings:CUSTOMER')}`
                }
            />
            {!isLoading && (
                <Typography variant="subtitle2" color="text.secondary" mb={3}>
                    {translate('customer-settings:ID')}: {customerInfo?._id}
                </Typography>
            )}

            {isLoading ? (
                <SkeletonTable />
            ) : !Object.keys(data).length ? (
                <Typography>{translate('customer-settings:NO_SETTINGS')}</Typography>
            ) : (
                <CustomerSettingsList
                    data={data}
                    handleValueChange={handleValueChange}
                    validationErrors={validationErrors}
                    getDisplayValue={getDisplayValue}
                    formatValue={formatValue}
                />
            )}

            {!isLoading && (
                <Box sx={{ textAlign: 'right', mt: 3 }}>
                    <LoadingButton
                        variant="contained"
                        loading={saving}
                        disabled={changedData.length === 0}
                        onClick={handleSave}
                    >
                        {translate('common:SAVE')}
                    </LoadingButton>
                </Box>
            )}
        </Fragment>
    );
};

export default CustomerSettings;
